import { Army } from "@/domain/army";
import { getIllustrationSrc, getImageUploadSrc } from "@/domain/s3-urls";
import { Unit } from "@/domain/unit";
import { ArmyIllustration, ImageUpload } from "@/schema/latest";
import {
  FormGroupLabel,
  FormRadio,
  FormRadioGroup,
  FormRadioProps,
  useDialogContext,
  useFormContext,
  VisuallyHidden,
} from "@ariakit/react";
import { ExternalLink, Failure, Success, useIsAuthenticated } from "@indietabletop/appkit";
import { RecipeVariants } from "@vanilla-extract/recipes";
import { forwardRef, ReactNode, useId, useRef, useState } from "react";
import { armyPhotoSize } from "../army-detail-page/imageSizes";
import { wrap } from "../army-form-dialog/wrap";
import { Button } from "../button";
import { useDatabase } from "../database-provider";
import { FormDialogV2 } from "../form-dialog";
import { useForm } from "../hooks/use-form";
import { Image } from "../image";
import { Link } from "../link";
import { color } from "../tokens";
import { button, interactiveText, interactiveTextWhite, primaryTheme } from "../utils.css";
import { illustrationsByAuthor, thumbSize } from "./illustrations";
import * as css from "./style.css";
import { uploadStagedImages } from "./uploadImages";
import { useStagedImage } from "./use-staged-image";

export type StagedImageUpload = {
  type: "STAGED";
  file: File;
  src: string;
};

export type SelectedArmyImage = StagedImageUpload | ImageUpload | ArmyIllustration;

function IllustrationGroup(props: {
  label: ReactNode;
  note: string;
  name: FormRadioProps["name"];
  options: string[];
  href: string;
}) {
  const [isCollapsed, setCollapsed] = useState(props.options.length > 8);
  const form = useFormContext();

  return (
    <FormRadioGroup>
      <FormGroupLabel className={css.groupLabel}>
        <ExternalLink href={props.href} className={interactiveText}>
          {props.label}
        </ExternalLink>
      </FormGroupLabel>

      <p className={css.groupNote}>{props.note}</p>

      <div className={css.groupRadiosGrid({ isCollapsed })}>
        {isCollapsed && (
          <div
            style={{
              position: "absolute",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              inset: "auto 0 0 0",
              blockSize: "8rem",
              textAlign: "center",
              zIndex: "1",
              backgroundImage: "linear-gradient(transparent, white)",
            }}
          >
            <Button
              onClick={() => setCollapsed(false)}
              className={button}
              style={{ maxInlineSize: "10rem", ...primaryTheme }}
            >
              Show more
            </Button>
          </div>
        )}

        {props.options.map((illustrationId) => {
          return (
            <FormRadio
              key={illustrationId}
              value={illustrationId}
              name={props.name}
              onDoubleClick={() => void form?.submit()}
              render={
                <div className={css.radio({ aspectRatio: "1/1" })}>
                  <Image
                    {...thumbSize}
                    src={getIllustrationSrc(illustrationId)}
                    optimize
                    alt=""
                    style={{
                      inlineSize: "100%",
                      blockSize: "100%",
                      objectFit: "contain",
                      padding: "0.375rem",
                    }}
                  />
                  <div className={css.selectionFrame()} />
                </div>
              }
            />
          );
        })}
      </div>
    </FormRadioGroup>
  );
}

const SelectedImagePreview = forwardRef<
  HTMLDivElement,
  { stagedImage: ImageUpload | StagedImageUpload } & RecipeVariants<typeof css.imagePreview>
>(function SelectedImagePreview(props, ref) {
  const { stagedImage, aspectRatio, ...rest } = props;
  const src = stagedImage.type === "STAGED" ? stagedImage.src : getImageUploadSrc(stagedImage.src);

  return (
    <div {...rest} ref={ref} className={css.radio({ aspectRatio })}>
      <Image
        {...armyPhotoSize}
        src={src}
        className={css.imagePreview({ aspectRatio })}
        // Don't optimize staged images — they are local!
        optimize={stagedImage.type === "IMAGE_UPLOAD"}
      />
      <span className={css.selectionFrame()} />
    </div>
  );
});

export function ImageUploadButton(props: {
  setStagedImage: (image: ImageUpload | StagedImageUpload | null) => void;
  previewImage: ReactNode;
  placeholderImage?: ReactNode;
  onSelect?: () => void;
  onUnselect?: () => void;
}) {
  const id = useId();
  const ref = useRef<HTMLInputElement | null>(null);
  const isAuthenticated = useIsAuthenticated();

  return (
    <div className={css.imageUpload}>
      <VisuallyHidden>
        <input
          id={id}
          ref={ref}
          type="file"
          accept="image/*"
          onChange={(event) => {
            const file = event.target.files?.item(0);
            if (!file) {
              return;
            }

            props.setStagedImage({
              type: "STAGED",
              file,
              src: URL.createObjectURL(file),
            });

            props.onSelect?.();
          }}
        />
      </VisuallyHidden>

      {props.previewImage ?? props.placeholderImage}

      {isAuthenticated ?
        <label
          htmlFor={id}
          className={button}
          // style={!props.stagedImage ? primaryTheme : undefined}
        >
          {props.previewImage ? "Choose another" : "Choose from device"}
        </label>
      : <AccountRequiredNote />}

      {props.previewImage && (
        <Button
          className={button}
          onClick={() => {
            props.setStagedImage(null);
            props.onUnselect?.();

            // Clear hidden file input field so that if a file is selected again it actually shows.
            // (If left uncleared, the 'change' even would not fire after re-selection...)
            if (ref.current) {
              ref.current.value = "";
            }
          }}
        >
          Clear
        </Button>
      )}
    </div>
  );
}

function AccountRequiredNote(): ReactNode {
  return (
    <div className={css.lockedButton}>
      <div>
        <div className={css.lockedHeading}>Choose from device</div>
        <div className={css.lockedInfo}>
          {"A "}
          <span className={css.infoHighlight}>free</span>
          {" Indie Tabletop Club account is needed to upload images. "}
          <Link className={interactiveTextWhite} href="~/join">
            Join now
          </Link>
          {" or "}
          <Link className={interactiveTextWhite} href="~/login">
            log in
          </Link>
          .
        </div>
      </div>

      <svg
        className={css.lockedIcon}
        width="40px"
        height="40px"
        viewBox="0 0 40 40"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g stroke="none" strokeWidth="1" fill="white" fillRule="evenodd">
          <path d="M19.75,9 C17.67893,9 16,10.67893 16,12.75 L16,16.13666 C14.19102,17.3476 13,19.4097 13,21.75 C13,25.4779 16.02208,28.5 19.75,28.5 C23.4779,28.5 26.5,25.4779 26.5,21.75 C26.5,19.4097 25.309,17.3476 23.5,16.13666 L23.5,12.75 C23.5,10.67893 21.8211,9 19.75,9 Z M22,15.38409 L22,12.75 C22,11.50736 20.9926,10.5 19.75,10.5 C18.5074,10.5 17.5,11.50736 17.5,12.75 L17.5,15.38409 C18.2038,15.13535 18.9611,15 19.75,15 C20.5389,15 21.2962,15.13535 22,15.38409 Z M18,19.75 C18,18.7835 18.7835,18 19.75,18 C20.7165,18 21.5,18.7835 21.5,19.75 C21.5,20.4481 21.0912,21.0507 20.5,21.3316 L20.5,22.75 C20.5,23.1642 20.1642,23.5 19.75,23.5 C19.3358,23.5 19,23.1642 19,22.75 L19,21.3316 C18.4088,21.0507 18,20.4481 18,19.75 Z"></path>
        </g>
      </svg>
    </div>
  );
}

type MainImageFormProps = {
  image: SelectedArmyImage | null;
  onSuccess?: (image: SelectedArmyImage | null) => void;
  onSubmit?: OnMainImageSubmit;
};

function useMainImageForm(props: MainImageFormProps) {
  const { image, onSuccess, onSubmit = (image) => new Success(image) } = props;

  const dialog = useDialogContext();

  const { stagedImage, setStagedImage } = useStagedImage(
    image && image.type !== "ILLUSTRATION" ? image : null,
  );

  const valuesToPayload = (values: { illustrationId: string }): SelectedArmyImage | null => {
    // If illustration ID is falsy, it means the user has cleared the field -- they don't want
    // any army illustration.
    if (!values.illustrationId) {
      return null;
    }

    // If illustration ID is CUSTOM, it means they are uploading their own image. We pass the
    // currently staged image.
    if (values.illustrationId === "CUSTOM") {
      return stagedImage;
    }

    return {
      type: "ILLUSTRATION",
      id: values.illustrationId,
    };
  };

  const illustrationId =
    image ?
      image.type === "ILLUSTRATION" ?
        image.id
      : "CUSTOM"
    : "";

  const { form, submitName } = useForm({
    defaultValues: {
      illustrationId: illustrationId,
    },

    async onSubmit({ values }) {
      return await onSubmit(valuesToPayload(values));
    },

    onSuccess(image) {
      onSuccess?.(image);
      dialog?.hide();
    },
  });

  return {
    form,
    submitName,
    stagedImage,
    setStagedImage,
    setCustomImage: () => form.setValue(form.names.illustrationId, "CUSTOM"),
    clearImage: () => form.setValue(form.names.illustrationId, ""),
  };
}

type OnMainImageSubmit = (
  image: SelectedArmyImage | null,
) => Promise<Success<SelectedArmyImage | null> | Failure<string>>;

function MainImageDialogBase(
  props: MainImageFormProps & {
    submitLabel: string;
  },
) {
  const { submitLabel } = props;
  const { form, submitName, stagedImage, setStagedImage, setCustomImage, clearImage } =
    useMainImageForm(props);

  return (
    <FormDialogV2
      form={form}
      heading="Main Image"
      submitLabel={submitLabel}
      formSubmitName={submitName}
    >
      <ImageUploadButton
        onSelect={setCustomImage}
        onUnselect={clearImage}
        setStagedImage={setStagedImage}
        previewImage={
          stagedImage?.type === "STAGED" || stagedImage?.type === "IMAGE_UPLOAD" ?
            <FormRadio
              name={form.names.illustrationId}
              value="CUSTOM"
              render={<SelectedImagePreview stagedImage={stagedImage} />}
              onDoubleClick={() => {
                void form?.submit();
              }}
            />
          : null
        }
      />

      <div className={css.divider}>— or —</div>

      <div className={css.groupsStack}>
        <IllustrationGroup
          label="Illustrations by CROM"
          note="Rulebook illustrations."
          href="https://crom.ink"
          name={form.names.illustrationId}
          options={illustrationsByAuthor.crom}
        />

        <IllustrationGroup
          label="Illustrations by Creyates"
          note="Hobgoblin community illustrations. Used with permission."
          href="https://www.artstation.com/creyates"
          name={form.names.illustrationId}
          options={illustrationsByAuthor.creyates}
        />
      </div>
    </FormDialogV2>
  );
}

export function MainImageSelectDialog(props: {
  image: SelectedArmyImage | null;
  onSuccess: (image: SelectedArmyImage | null) => void;
}) {
  return <MainImageDialogBase {...props} submitLabel="Confirm selection" />;
}

export function MainImageFormDialog(props: { army: Army }) {
  const { army } = props;
  const database = useDatabase();
  return (
    <MainImageDialogBase
      image={army.mainImage}
      submitLabel="Save"
      onSubmit={async (mainImage) => {
        const imagesOp = await uploadStagedImages([mainImage]);
        if (imagesOp.isFailure) {
          return imagesOp;
        }

        const updateOp = await wrap(
          database.updateArmy({ ...army.data, images: imagesOp.valueOrNull() ?? [] }),
        );
        if (updateOp.isFailure) {
          return updateOp;
        }

        return new Success(mainImage);
      }}
    />
  );
}

export function PosterImageFormDialog(props: { army: Army }) {
  const { army } = props;
  const database = useDatabase();

  const dialog = useDialogContext();

  const [stagedImage, setStagedImage] = useState<StagedImageUpload | ImageUpload | null>(
    army.posterImage,
  );

  const { form, submitName } = useForm({
    defaultValues: {},
    async onSubmit() {
      const posterImageOp = await uploadStagedImages([stagedImage]);
      if (posterImageOp.isFailure) {
        return posterImageOp;
      }

      return wrap(
        database.updateArmy({
          ...army.data,
          posterImages: posterImageOp.valueOrNull(),
        }),
      );
    },
    onSuccess() {
      dialog?.hide();
    },
  });

  return (
    <FormDialogV2
      form={form}
      submitLabel="Save"
      heading="Army Poster Image"
      formSubmitName={submitName}
    >
      <ImageUploadButton
        previewImage={
          stagedImage && <SelectedImagePreview stagedImage={stagedImage} aspectRatio="3/4" />
        }
        setStagedImage={setStagedImage}
        placeholderImage={<PlaceholderImage aspectRatio="3/4" dimensions="1200 × 1600px" />}
      />
    </FormDialogV2>
  );
}

function PlaceholderImage(props: { aspectRatio: "16/9" | "3/4"; dimensions: string }) {
  const { aspectRatio, dimensions } = props;

  const orientations: Record<typeof aspectRatio, string> = {
    "16/9": "horizontal",
    "3/4": "vertical",
  };
  const orientation = orientations[aspectRatio];

  const inlineSizes: Record<typeof aspectRatio, string> = {
    "16/9": "5rem",
    "3/4": "3rem",
  };
  const inlineSize = inlineSizes[aspectRatio];

  return (
    <div className={css.imagePlaceholder}>
      <div style={{ display: "flex", padding: "0.75rem 0 0" }}>
        <div
          style={{
            backgroundColor: "hsl(0 0% 98%)",
            aspectRatio: aspectRatio,
            inlineSize: inlineSize,
            borderRadius: "0.25rem",
            boxShadow: "0 0.125rem 0.5rem hsl(0 0% 0% / 0.05)",
            rotate: "-5deg",
            translate: "0 -0.5rem",
          }}
        />
        <div
          style={{
            backgroundColor: "white",
            aspectRatio: aspectRatio,
            inlineSize: inlineSize,
            marginInlineStart: `calc(${inlineSize} * -0.75)`,
            borderRadius: "0.25rem",
            boxShadow: "0 0.125rem 0.5rem hsl(0 0% 0% / 0.05)",
            rotate: "5deg",
            translate: "0 0.125rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.875rem",
            color: color.DARK_GRAY,
          }}
        >
          {aspectRatio.replace("/", " : ")}
        </div>
      </div>
      <div className={css.imagePlaceholderNote}>
        For best results, use a {orientation} image around{" "}
        <strong style={{ color: "black" }}>{dimensions}</strong>. Must be under{" "}
        <strong>5 MB</strong>.
      </div>
    </div>
  );
}

export function UnitImageDialog(props: { unit: Unit }) {
  const { unit } = props;

  const database = useDatabase();
  const dialog = useDialogContext();

  const [stagedImage, setStagedImage] = useState<StagedImageUpload | ImageUpload | null>(
    props.unit.image,
  );

  const { form, submitName } = useForm({
    defaultValues: {},
    async onSubmit() {
      const imageUpload = await uploadStagedImages([stagedImage]);
      if (imageUpload.isFailure) {
        return imageUpload;
      }

      const images = imageUpload.valueOrNull();
      return wrap(database.updateUnit(unit.army.id, unit.id, { images }));
    },
    onSuccess() {
      dialog?.hide();
    },
  });

  return (
    <FormDialogV2 form={form} submitLabel="Save" heading="Unit Image" formSubmitName={submitName}>
      <ImageUploadButton
        previewImage={stagedImage && <SelectedImagePreview stagedImage={stagedImage} />}
        setStagedImage={setStagedImage}
        placeholderImage={<PlaceholderImage aspectRatio="16/9" dimensions="1600 × 900px" />}
      />
    </FormDialogV2>
  );
}
